import React, { useMemo } from 'react';
import {
  Popover,
  Box,
  IconButton,
} from '@mui/material';

interface ColorSwatchModalProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onColorSelect: (color: string) => void;
  currentColor?: string;
}

// Estimated popover height (padding + swatch size + arrow)
const POPOVER_HEIGHT = 70;

const ColorSwatchModal: React.FC<ColorSwatchModalProps> = ({
  open,
  anchorEl,
  onClose,
  onColorSelect,
  currentColor,
}) => {
  const colors = [
    { name: 'default', color: 'transparent' },
    { name: '#26a69a', color: '#26a69a' },
    { name: '#7b1fa2', color: '#7b1fa2' },
    { name: '#f44336', color: '#f44336' },
  ];

  const handleColorClick = (colorName: string) => {
    onColorSelect(colorName === 'default' ? '' : colorName);
    onClose();
  };

  // Determine whether to show the popover above or below the anchor
  const openAbove = useMemo(() => {
    if (!anchorEl) return true;
    const rect = anchorEl.getBoundingClientRect();
    // Show above if there's enough room; otherwise show below
    return rect.top >= POPOVER_HEIGHT;
  }, [anchorEl]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: openAbove ? 'top' : 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: openAbove ? 'bottom' : 'top',
        horizontal: 'center',
      }}
      disableScrollLock={true}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      disableRestoreFocus={true}
      marginThreshold={16}
      slotProps={{
        paper: {
          sx: (theme) => ({
            borderRadius: 2,
            p: 1.5,
            boxShadow: 3,
            ...(openAbove ? { mt: -1 } : { mt: 1 }),
            overflow: 'visible',
            bgcolor: 'background.paper',
            // Arrow pointing down (popover is above the item)
            ...(openAbove ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: `10px solid ${theme.palette.background.paper}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '11px solid transparent',
                borderRight: '11px solid transparent',
                borderTop: '11px solid',
                borderTopColor: 'rgba(0, 0, 0, 0.12)',
                zIndex: -1,
              },
            } : {
              // Arrow pointing up (popover is below the item)
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderBottom: `10px solid ${theme.palette.background.paper}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '11px solid transparent',
                borderRight: '11px solid transparent',
                borderBottom: '11px solid',
                borderBottomColor: 'rgba(0, 0, 0, 0.12)',
                zIndex: -1,
              },
            }),
          })
        }
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {colors.map(({ name, color }) => (
          <IconButton
            key={name}
            onClick={() => handleColorClick(name)}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: color,
              border: '2px solid #ccc',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: color,
                transform: 'scale(1.15)',
                boxShadow: 2,
              },
              transition: 'transform 0.2s',
              ...(currentColor === name || (currentColor === '' && name === 'default') || (!currentColor && name === 'default')
                ? {
                    boxShadow: 2,
                    outline: '2px solid #1976d2',
                  }
                : {}),
            }}
          />
        ))}
      </Box>
    </Popover>
  );
};

export default ColorSwatchModal;
